import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import {
    Avatar, Box, Button, Dialog, DialogContent, Grid, IconButton,
    LinearProgress, linearProgressClasses, styled, Tab, Tabs,
    TextField, Typography, useTheme, Chip, Stack
} from "@mui/material";
import {
    ApplicationStatus, ExamType, LoginType, ProgressStatus,
    ProjectDisplayStatus, ProjectStatus, removeEmptyFields,
    ServiceOffer, StudentGrade
} from "../../common/const";
import theme from "../../theme";
import {
    ArrowBack, ChatBubbleOutlineOutlined, Check, Close,
    Person, WatchLater, WatchLaterOutlined, CalendarToday, OpenInNew
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import CommonCard from "../../common/components/CommonCard";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { getStudentData, getUser, updateStudent } from "../../api/authApi";
import SessionHistory from "./SessionHistory";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import MileStone from "../Project Details/MileStone";
import WorkLogs from "../Project Details/WorkLogs";
import Progress from "../Dcp Tutoring/Progress";
import Events from "./Events";
import Tasks from "../Dcp Tutoring/Tasks";
import ScoreHistory from "../SAT ACT Coach/ScoreHistory";
import { getDashboardGraph } from "../../api/satactApi";
import CoachTask from "../SAT ACT Coach/CoachTask";
import CommonSelect from "../../common/components/CommonSelect";
import CollegeList from "./CollegeList";
import { getCounsellingColleges } from "../../api/collegeApi";
import ProjectWorkSpace from "./ProjectWorkSpace";
import SatActMentor from "./SatActMentor";
import ApAcademicTutoring from "./ApAcademicTutoring";
import Counselling from "./Counselling";
import SkillCoaching from "./SkillCoaching";
import CommonFormField from "../../common/components/CommonFormField";
import { confirmBooking, getEventByInvite } from "../../api/sessionApi";
import moment from "moment";
import CommonBookSession from "../../common/components/CommonBookSession";
import { getAllAssignSubject } from "../../api/subjectApi";
import { getCounsellorDashboard, getInsights, updateInsight } from "../../api/mentorApi";
import { joinConversation } from "../../api/chatApi";
import { getMentorProject } from "../../api/projectApi";
import { getMileStone } from "../../api/mileStoneApi";

// ─── Styled Components ────────────────────────────────────────────────────────

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: 5,
    marginTop: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[200],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.secondary.main,
    },
}));

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '560px',
        width: '100%',
        margin: '16px',
    },
});

/**
 * ActionButton — compact button that NEVER wraps text.
 * Use the `variant` prop: "primary-filled" | "outlined-navy" | "ghost"
 */
const ActionButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme, variant: v }) => ({
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',           // ← key fix: no text wrap
    borderRadius: '8px',
    fontSize: '0.8125rem',
    lineHeight: 1.2,
    padding: '8px 14px',
    minWidth: 0,
    flexShrink: 0,
    ...(v === 'primary-filled' && {
        backgroundColor: theme.palette.secondary.main,
        color: '#fff',
        border: 'none',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    }),
    ...(v === 'outlined-navy' && {
        border: `1.5px solid ${theme.palette.primary.main}`,
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: `${theme.palette.primary.main}10`,
        },
    }),
    ...(v === 'ghost' && {
        border: '1.5px solid #E5E7EB',
        color: '#374151',
        backgroundColor: '#FAFAFA',
        '&:hover': { backgroundColor: '#F3F4F6' },
    }),
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',           // ← tabs never wrap
    minWidth: 0,
    padding: '10px 16px',
    color: '#6B7280',
    '&.Mui-selected': {
        color: theme.palette.secondary.main,
        fontWeight: 600,
    },
}));

const StatCard = styled(Box)(() => ({
    backgroundColor: '#FAFAFA',
    borderRadius: '12px',
    border: '1px solid #F3F4F6',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
}));

const IconCircle = styled(Box)(({ bg }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: bg,
    width: 44,
    height: 44,
    flexShrink: 0,
}));

// ─── Component ────────────────────────────────────────────────────────────────

const StudentDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const muiTheme = useTheme();

    const { loading, user } = useSelector((state) => state.auth);
    const [previewUrl, setPreviewUrl] = useState('');
    const [userData, setUserData] = useState(null);

    const studentId = location.pathname.split('/')[2]?.split('&')[0];
    const serviceType = location.pathname.split('/')[2]?.split('&')[1]?.split(',');
    const studentMentorId = location.pathname.split('/')[2]?.split('&')[2];

    const resolveInitialTab = () => {
        if (serviceType.includes(ServiceOffer.mentor)) return 0;
        if (serviceType.includes(ServiceOffer.coach)) return 2;
        if (serviceType.includes(ServiceOffer.academic)) return 5;
        if (serviceType.includes(ServiceOffer.tutor)) return 1;
        if (serviceType.includes(ServiceOffer.counsellor)) return 4;
        return 3;
    };

    const resolveInitialService = () => {
        if (serviceType.includes(ServiceOffer.mentor)) return ServiceOffer.mentor;
        if (serviceType.includes(ServiceOffer.coach)) return ServiceOffer.coach;
        if (serviceType.includes(ServiceOffer.tutor)) return ServiceOffer.tutor;
        if (serviceType.includes(ServiceOffer.academic)) return ServiceOffer.academic;
        if (serviceType.includes(ServiceOffer.counsellor)) return ServiceOffer.counsellor;
        return ServiceOffer.skillCoach;
    };

    const [activeTab, setActiveTab] = useState(resolveInitialTab());
    const [testType, setTestType] = useState(ExamType.SAT);
    const [state, setState] = useState({
        series: [{ name: ExamType.SAT, data: [] }],
        options: {
            stroke: { width: 2, curve: 'smooth' },
            chart: { height: 350, type: 'line', zoom: { enabled: false }, toolbar: { show: false } },
            dataLabels: { enabled: true },
            xaxis: { categories: [] },
            grid: {
                borderColor: '#e7e7e7', strokeDashArray: 5,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: true } },
            },
        },
    });

    const [applicationStatus, setApplicationStatus] = useState(null);
    const [progressStatus, setProgressStatus] = useState(null);
    const [explanation, setExplanation] = useState(null);
    const [colleges, setWishListCollege] = useState([]);
    const [viewMore, setViewMore] = useState(false);
    const [currentGrade, setStudntGrade] = useState(null);
    const [currentService, setCurrentService] = useState(resolveInitialService());
    const [open, setOpen] = useState(false);
    const [initialEventData, setInitialEventData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [bookDialog, setBookDialog] = useState(false);
    const [sessionNote, setSessionNote] = useState('');
    const [assignSkill, setAssignSkill] = useState([]);
    const [subService, setSubService] = useState(null);
    const [stat, setStat] = useState(null);
    const [projectData, setProjectData] = useState(null);
    const [mileStones, setMileStones] = useState([]);

    // ─── API helpers ─────────────────────────────────────────────────────────

    const errMsg = (err) =>
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.message ||
        err?.response?.data?.error;

    const getEventData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getEventByInvite(initialEventData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookDialog(true);
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (initialEventData !== null) {
            dispatch(setLoading({ loading: true }));
            setTimeout(() => { getEventData(); }, 5000);
        }
    }, [initialEventData]);

    const handleCloseAll = () => {
        setBookDialog(false);
        setSessionNote('');
        setEventData(null);
    };

    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const data = {
                mentor_profile_id: user?.mentor?.mentor_profile_id,
                student_id: studentId,
                service_offers: [currentService],
                notes: sessionNote,
                status: "CONFIRMED",
                invitee_email: eventData.invitee_email,
                calendly_event_uri: eventData.calendly_event_uri,
                calendly_invitee_uri: eventData.calendly_invitee_uri,
                student_subject_id: subService,
                project_id: projectData?.project_id,
            };
            const response = await confirmBooking(removeEmptyFields(data));
            if (response) {
                setBookDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialEventData(null);
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    const getStudent = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getStudentData(studentId);
            setApplicationStatus(response?.data?.application_status);
            setStudntGrade(response?.data?.student_current_grade);
            if (response?.data?.user_id) {
                const userResponse = await getUser(response?.data?.user_id);
                setUserData(userResponse?.data);
                if (userResponse?.data?.avatar) {
                    if (userResponse?.data.login_type === LoginType.EMAIL) {
                        const res = await fetch(
                            `${process.env.REACT_APP_BASE_URL}/file?bname=${userResponse?.data.avatar}&download=false`,
                            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
                        );
                        const blob = await res.blob();
                        setPreviewUrl(URL.createObjectURL(blob) || '');
                    } else {
                        setPreviewUrl(userResponse?.data.avatar);
                    }
                }
            }
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (location.pathname.split('/')[2]) getStudent();
    }, [location]);

    const tabServiceMap = {
        0: ServiceOffer.mentor,
        1: ServiceOffer.tutor,
        2: ServiceOffer.coach,
        3: ServiceOffer.skillCoach,
        4: ServiceOffer.counsellor,
        5: ServiceOffer.academic,
    };

    const handleChange = (event, newValue) => {
        setCurrentService(tabServiceMap[newValue]);
        if (newValue !== 3) setSubService(null);
        setActiveTab(newValue);
    };

    const getHistoryData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getDashboardGraph(testType, studentId);
            setState({
                series: [{ name: testType, data: response.data?.map((a) => a.score) }],
                options: {
                    stroke: { width: 2, curve: 'smooth' },
                    chart: { height: 350, type: 'line', zoom: { enabled: false }, toolbar: { show: false } },
                    dataLabels: { enabled: true },
                    xaxis: { categories: response.data?.map((a) => a.label) },
                    grid: {
                        borderColor: '#e7e7e7', strokeDashArray: 5,
                        yaxis: { lines: { show: true } },
                        xaxis: { lines: { show: true } },
                    },
                }
            });
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (location.pathname.split('/')[2]) getHistoryData();
    }, [location, testType]);

    const getAllWishListColleges = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getCounsellingColleges(studentId);
            setWishListCollege(response.data);
        } catch (err) {
            setWishListCollege([]);
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (studentId && currentService === ServiceOffer.counsellor) getAllWishListColleges();
    }, [studentId, currentService]);

    const getAssignedSubject = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getAllAssignSubject(ServiceOffer.skillCoach, studentId);
            setSubService(response?.data?.[0]?.student_subject_id);
            setAssignSkill(response?.data);
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    const getCounsellorData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getCounsellorDashboard(studentId);
            setStat(response.data);
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    const getProjectData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getMentorProject(studentId, user?.mentor?.mentor_profile_id);
            setProjectData(response?.data);
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (activeTab === 3) getAssignedSubject();
        else if (activeTab === 4) { getInsightData(); getCounsellorData(); }
        else if (activeTab === 0 && user?.mentor) getProjectData();
    }, [activeTab, user]);

    const getInsightData = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getInsights(studentId, user?.mentor?.mentor_profile_id);
            setProgressStatus(response?.data?.counsellor_provided_status);
            setExplanation(response?.data?.counsellor_suggestion);
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    const getMileStones = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getMileStone(projectData?.project_id);
            setMileStones(response?.data);
        } catch (err) {
            showNotification(errMsg(err), 'error');
        } finally {
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        if (projectData?.project_id) getMileStones();
    }, [projectData]);

    const viewMoreLabel = {
        [ServiceOffer.mentor]: 'Project Workspace',
        [ServiceOffer.coach]: 'SAT/ACT Coaching',
        [ServiceOffer.academic]: 'Academic Tutoring',
        [ServiceOffer.tutor]: 'AP Tutoring',
        [ServiceOffer.counsellor]: 'Counselling',
        [ServiceOffer.skillCoach]: 'Skill Coaching',
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <Fragment>
            {loading && <LoadingSpinner />}

            {/* ── Book Session Confirmation Dialog ── */}
            <StyledDialog open={bookDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseAll}
                        size="small"
                        sx={{ position: 'absolute', right: 12, top: 12, color: '#9CA3AF' }}
                    >
                        <Close fontSize="small" />
                    </IconButton>

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, pr: 3 }}>
                        Confirm Your Session
                    </Typography>

                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <CalendarToday sx={{ color: '#6B7280', fontSize: 18, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                        {moment(eventData?.session_start_time).format('ddd, MMM D YYYY')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Date</Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <WatchLater sx={{ color: '#6B7280', fontSize: 18, flexShrink: 0 }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                        {moment(eventData?.session_start_time).format('hh:mm A')} – {moment(eventData?.session_end_time).format('hh:mm A')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Time</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Typography variant="body2" sx={{ color: '#374151', mb: 1, fontWeight: 500 }}>
                        Any specific question for this session?
                    </Typography>
                    <TextField
                        fullWidth multiline rows={4}
                        placeholder="Write here…"
                        value={sessionNote}
                        onChange={(e) => setSessionNote(e.target.value)}
                        sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />

                    <Button
                        fullWidth onClick={handleConfirmBooking}
                        sx={{
                            backgroundColor: '#1E3A8A', color: 'white',
                            textTransform: 'none', borderRadius: '10px',
                            height: 48, fontWeight: 600, fontSize: '0.9375rem',
                            '&:hover': { backgroundColor: '#1E40AF' },
                        }}
                    >
                        Confirm Booking
                    </Button>
                </DialogContent>
            </StyledDialog>

            {/* ── Calendly widget ── */}
            <CommonBookSession
                bookingUrl={process.env.REACT_APP_CALENDLY_SESSION_URL}
                open={open}
                setOpen={setOpen}
                studentName={`${userData?.first_name} ${userData?.last_name}`}
                email={userData?.email}
                tutorName={`${user?.mentor?.user?.first_name || ''} ${user?.mentor?.user?.last_name || ''}`.trim()}
                mentorProfileId={user?.mentor?.mentor_profile_id}
                handleClose={(data) => { setOpen(false); setInitialEventData(data); }}
            />

            {/* ── Page Header ── */}
            <PageHeader
                title={
                    <Box
                        sx={{ py: { xs: 0.5, sm: 1 }, display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}
                        onClick={() => viewMore ? setViewMore(false) : navigate('/my-students')}
                    >
                        <ArrowBack sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 18 } }}>
                            {viewMore ? viewMoreLabel[currentService] : 'Student Detail View'}
                        </Typography>
                    </Box>
                }
            />

            {/* ── Main Grid ── */}
            <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="flex-start">

                {/* ═══ LEFT COLUMN ═══ */}
                <Grid item xs={12} md={4} lg={3.5}>

                    {/* Student Info Card */}
                    <CommonCard
                        paddingBottom="0px"
                        padding="0px"
                        borderRadius={2}
                        content={
                            <Box p={{ xs: 2, sm: 2.5 }}>
                                {/* Avatar + Identity */}
                                <Box display="flex" gap={2} alignItems="flex-start">
                                    <Avatar
                                        src={previewUrl}
                                        sx={{
                                            width: { xs: 54, sm: 64 },
                                            height: { xs: 54, sm: 64 },
                                            flexShrink: 0,
                                            backgroundColor: theme.palette.grey[200],
                                        }}
                                    >
                                        <Person sx={{ fontSize: 30, color: theme.palette.grey[400] }} />
                                    </Avatar>

                                    <Box flex={1} minWidth={0}>
                                        <Typography
                                            fontWeight={700}
                                            fontSize={{ xs: 15, sm: 16 }}
                                            noWrap
                                        >
                                            {userData?.first_name} {userData?.last_name}
                                        </Typography>
                                        <Typography
                                            color="text.secondary"
                                            fontSize={12}
                                            sx={{ wordBreak: 'break-all', lineHeight: 1.4, mt: 0.25 }}
                                        >
                                            {userData?.email}
                                        </Typography>

                                        {/* Grade chip */}
                                        {((currentService === ServiceOffer.academic || currentService === ServiceOffer.tutor) && viewMore) && (
                                            <Chip
                                                label={StudentGrade?.find((a) => a.value === currentGrade)?.label}
                                                size="small"
                                                sx={{
                                                    mt: 0.75,
                                                    backgroundColor: `${theme.palette.secondary.main}18`,
                                                    color: theme.palette.secondary.main,
                                                    fontWeight: 600,
                                                    fontSize: 11,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {/* Project progress (mentor view) */}
                                {currentService === ServiceOffer.mentor && viewMore && projectData && (
                                    <Box mt={2} p={1.5} borderRadius={2} sx={{ backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6' }}>
                                        <Typography fontWeight={600} fontSize={13} noWrap>
                                            {projectData.project_name}
                                        </Typography>
                                        <Box
                                            sx={{
                                                mt: 0.75, mb: 1,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 1.25, py: 0.3,
                                                borderRadius: '6px',
                                                backgroundColor:
                                                    projectData.status === ProjectStatus.InProgress
                                                        ? theme.palette.secondary.main
                                                        : projectData.status === ProjectStatus.Completed
                                                            ? '#01875F'
                                                            : '#E5E7EB',
                                            }}
                                        >
                                            <Typography
                                                fontSize={11}
                                                fontWeight={600}
                                                color={
                                                    projectData.status === ProjectStatus.InProgress
                                                        ? theme.palette.primary.main
                                                        : projectData.status === ProjectStatus.Completed
                                                            ? '#fff'
                                                            : '#374151'
                                                }
                                            >
                                                {ProjectDisplayStatus[projectData.status]}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography color="text.secondary" fontSize={12}>Progress</Typography>
                                            <Typography color="text.secondary" fontSize={12} fontWeight={600}>
                                                {projectData.progress || 0}%
                                            </Typography>
                                        </Box>
                                        <BorderLinearProgress variant="determinate" value={projectData.progress || 0} />
                                    </Box>
                                )}

                                {/* Action Buttons — Stack that wraps gracefully */}
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    mt={2}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    {/* Message */}
                                    <ActionButton
                                        variant="primary-filled"
                                        startIcon={<ChatBubbleOutlineOutlined sx={{ fontSize: '15px !important' }} />}
                                        onClick={async () => {
                                            try {
                                                dispatch(setLoading({ loading: true }));
                                                const response = await joinConversation({
                                                    student_id: userData?.user_id,
                                                    mentor_id: user?.user_id,
                                                });
                                                if (response.success) {
                                                    navigate(`/communication-hub?id=${userData?.user_id}`);
                                                }
                                            } catch (err) {
                                                showNotification(errMsg(err), 'error');
                                            } finally {
                                                dispatch(setLoading({ loading: false }));
                                            }
                                        }}
                                        sx={{ flex: '1 1 auto', minWidth: '90px' }}
                                    >
                                        Message
                                    </ActionButton>

                                    {/* Book Session */}
                                    {!viewMore && (
                                        <ActionButton
                                            variant="outlined-navy"
                                            onClick={() => setOpen(true)}
                                            sx={{ flex: '1 1 auto', minWidth: '105px' }}
                                        >
                                            Book Session
                                        </ActionButton>
                                    )}

                                    {/* Open Workspace */}
                                    {currentService === ServiceOffer.mentor && viewMore && (
                                        <ActionButton
                                            variant="outlined-navy"
                                            startIcon={<OpenInNew sx={{ fontSize: '14px !important' }} />}
                                            onClick={() => navigate(`/project-workspace?id=${projectData?.project_id}&sid=${studentId}`)}
                                            sx={{ flex: '1 1 auto', minWidth: '105px' }}
                                        >
                                            Workspace
                                        </ActionButton>
                                    )}
                                </Stack>

                                {/* Counsellor progress update panel */}
                                {viewMore && currentService === ServiceOffer.counsellor && (
                                    <Box mt={2} pt={2} borderTop="1px solid #F3F4F6">
                                        <Typography fontSize={13} fontWeight={600} mb={1.5}>
                                            Student Progress
                                        </Typography>
                                        <CommonSelect
                                            name="status"
                                            value={progressStatus}
                                            onChange={(e) => setProgressStatus(e.target.value)}
                                            placeholder="Select Progress Status"
                                            options={ProgressStatus}
                                        />
                                        <Box mt={1}>
                                            <CommonFormField
                                                type="email"
                                                name="name"
                                                value={explanation}
                                                onChange={(e) => setExplanation(e.target.value)}
                                                placeholder="e.g., You are on track for Early Decision deadlines"
                                            />
                                        </Box>
                                        <ActionButton
                                            variant="primary-filled"
                                            fullWidth
                                            sx={{ mt: 1.5, width: '100%' }}
                                            onClick={async () => {
                                                if (progressStatus && explanation) {
                                                    try {
                                                        dispatch(setLoading({ loading: true }));
                                                        const response = await updateInsight(studentMentorId, {
                                                            counsellor_provided_status: progressStatus,
                                                            counsellor_suggestion: explanation,
                                                        });
                                                        if (response?.success) {
                                                            showNotification(response?.message, 'success');
                                                            getInsightData();
                                                        }
                                                    } catch (err) {
                                                        showNotification(errMsg(err), 'error');
                                                    } finally {
                                                        dispatch(setLoading({ loading: false }));
                                                    }
                                                } else {
                                                    showNotification('Please select progress status and provide explanation', 'error');
                                                }
                                            }}
                                        >
                                            Update Status
                                        </ActionButton>
                                    </Box>
                                )}
                            </Box>
                        }
                    />

                    {/* Session History */}
                    {!viewMore && (
                        <Box mt={2}>
                            <SessionHistory
                                currentService={currentService}
                                studentId={studentId}
                                subService={subService || projectData?.project_id}
                                activeTab={activeTab}
                            />
                        </Box>
                    )}
                </Grid>

                {/* ═══ RIGHT COLUMN ═══ */}
                <Grid item xs={12} md={8} lg={8.5}>

                    {/* ── viewMore (full-screen sub-component) ── */}
                    {viewMore ? (
                        <CommonCard
                            paddingBottom="0px"
                            borderRadius={2}
                            content={
                                <Fragment>
                                    {currentService === ServiceOffer.mentor && (
                                        <ProjectWorkSpace studentId={studentId} currentService={currentService} projectData={projectData} mileStones={mileStones} getMileStones={getMileStones} />
                                    )}
                                    {currentService === ServiceOffer.coach && (
                                        <SatActMentor studentId={studentId} location={location} studentMentorId={studentMentorId} currentService={currentService} />
                                    )}
                                    {(currentService === ServiceOffer.tutor || currentService === ServiceOffer.academic) && (
                                        <ApAcademicTutoring studentId={studentId} currentService={currentService} currentGrade={currentGrade} />
                                    )}
                                    {currentService === ServiceOffer.counsellor && (
                                        <Counselling currentService={currentService} studentId={studentId} email={userData?.email} />
                                    )}
                                    {currentService === ServiceOffer.skillCoach && (
                                        <SkillCoaching currentService={currentService} studentId={studentId} />
                                    )}
                                </Fragment>
                            }
                        />
                    ) : (

                        /* ── Tab view ── */
                        <CommonCard
                            paddingBottom="0px"
                            borderRadius={2}
                            content={
                                <Fragment>
                                    {/* Tabs */}
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                        <Tabs
                                            value={activeTab}
                                            onChange={handleChange}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            allowScrollButtonsMobile
                                            sx={{
                                                minHeight: 44,
                                                '& .MuiTabs-indicator': {
                                                    backgroundColor: theme.palette.secondary.main,
                                                    height: 2,
                                                },
                                                // make scroll arrows narrower on small screens
                                                '& .MuiTabScrollButton-root': { width: 28 },
                                            }}
                                        >
                                            {serviceType.includes(ServiceOffer.mentor) && <StyledTab label="Project Workspace" value={0} />}
                                            {serviceType.includes(ServiceOffer.tutor) && <StyledTab label="AP Tutoring" value={1} />}
                                            {serviceType.includes(ServiceOffer.academic) && <StyledTab label="Academic Tutoring" value={5} />}
                                            {serviceType.includes(ServiceOffer.coach) && <StyledTab label="SAT/ACT Coaching" value={2} />}
                                            {serviceType.includes(ServiceOffer.skillCoach) && <StyledTab label="Skill Coaching" value={3} />}
                                            {serviceType.includes(ServiceOffer.counsellor) && <StyledTab label="Counselling" value={4} />}
                                        </Tabs>
                                    </Box>

                                    {/* ── Tab 0: Project Workspace ── */}
                                    {activeTab === 0 && (
                                        <Fragment>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
                                                <Typography fontSize={{ xs: 15, sm: 16 }} fontWeight={700} noWrap flex={1}>
                                                    {projectData?.project_name}
                                                </Typography>
                                                <ActionButton
                                                    variant="outlined-navy"
                                                    onClick={() => { setCurrentService(ServiceOffer.mentor); setViewMore(true); }}
                                                >
                                                    View More
                                                </ActionButton>
                                            </Box>
                                            <MileStone fromMentor getMileStones={getMileStones} mileStones={mileStones} />
                                            <WorkLogs fromMentor projectData={projectData} mileStones={mileStones} studentId={studentId} studentData={userData} />
                                        </Fragment>
                                    )}

                                    {/* ── Tabs 1, 3, 5: Tutoring / Skill Coaching ── */}
                                    {[1, 3, 5].includes(activeTab) && (
                                        <Fragment>
                                            {activeTab === 3 ? (
                                                /* Skill Coaching — chip strip + View More */
                                                <Box mb={2}>
                                                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                        {/* Horizontally scrollable chip row */}
                                                        <Box
                                                            sx={{
                                                                flex: 1,
                                                                display: 'flex',
                                                                gap: 1,
                                                                overflowX: 'auto',
                                                                pb: 0.5,
                                                                minWidth: 0,
                                                                // hide scrollbar visually
                                                                scrollbarWidth: 'none',
                                                                '&::-webkit-scrollbar': { display: 'none' },
                                                            }}
                                                        >
                                                            {assignSkill.map((a) => (
                                                                <ActionButton
                                                                    key={a.student_subject_id}
                                                                    variant={subService === a.student_subject_id ? 'outlined-navy' : 'ghost'}
                                                                    onClick={() => setSubService(a.student_subject_id)}
                                                                >
                                                                    {a?.subject?.name}
                                                                </ActionButton>
                                                            ))}
                                                        </Box>
                                                        <ActionButton
                                                            variant="outlined-navy"
                                                            onClick={() => { setCurrentService(ServiceOffer.skillCoach); setViewMore(true); }}
                                                        >
                                                            View More
                                                        </ActionButton>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                /* AP / Academic — just View More */
                                                <Box display="flex" justifyContent="flex-end" mb={2}>
                                                    <ActionButton
                                                        variant="outlined-navy"
                                                        onClick={() => {
                                                            setCurrentService(activeTab === 1 ? ServiceOffer.tutor : ServiceOffer.academic);
                                                            setViewMore(true);
                                                        }}
                                                    >
                                                        View More
                                                    </ActionButton>
                                                </Box>
                                            )}

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Events currentService={currentService} studentId={studentId} subService={subService} setInitialEventData={setInitialEventData} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <Tasks fromMentor currentService={currentService} studentId={studentId} subService={subService} />
                                                </Grid>
                                            </Grid>
                                            <Progress currentService={currentService} studentId={studentId} subService={subService} />
                                        </Fragment>
                                    )}

                                    {/* ── Tab 2: SAT/ACT Coaching ── */}
                                    {activeTab === 2 && (
                                        <Fragment>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                mb={2}
                                                gap={1}
                                                flexWrap="wrap"
                                            >
                                                {/* SAT / ACT toggle */}
                                                <Stack direction="row" spacing={1}>
                                                    {[ExamType.SAT, ExamType.ACT].map((type) => (
                                                        <ActionButton
                                                            key={type}
                                                            variant={testType === type ? 'outlined-navy' : 'ghost'}
                                                            onClick={() => setTestType(type)}
                                                            sx={{ minWidth: 60 }}
                                                        >
                                                            {type}
                                                        </ActionButton>
                                                    ))}
                                                </Stack>
                                                <ActionButton
                                                    variant="outlined-navy"
                                                    onClick={() => { setCurrentService(ServiceOffer.coach); setViewMore(true); }}
                                                >
                                                    View More
                                                </ActionButton>
                                            </Box>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <Events currentService={currentService} studentId={studentId} />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <CoachTask testType={testType} fromMentor studentId={studentId} />
                                                </Grid>
                                            </Grid>
                                            <ScoreHistory state={state} fromMentor />
                                        </Fragment>
                                    )}

                                    {/* ── Tab 4: Counselling ── */}
                                    {activeTab === 4 && (
                                        <Fragment>
                                            <Box display="flex" justifyContent="flex-end" mb={2}>
                                                <ActionButton
                                                    variant="outlined-navy"
                                                    onClick={() => { setCurrentService(ServiceOffer.counsellor); setViewMore(true); }}
                                                >
                                                    View More
                                                </ActionButton>
                                            </Box>

                                            <Grid container spacing={2} mb={2}>
                                                {/* Application Status */}
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Box sx={{ p: 2, backgroundColor: '#FAFAFA', borderRadius: '12px', border: '1px solid #F3F4F6', height: '100%' }}>
                                                        <Typography fontSize={13} fontWeight={600} mb={1.5}>
                                                            Application Status
                                                        </Typography>
                                                        <CommonSelect
                                                            name="serviceType"
                                                            value={applicationStatus}
                                                            onChange={(e) => setApplicationStatus(e.target.value)}
                                                            placeholder="e.g. Early Action"
                                                            options={ApplicationStatus}
                                                        />
                                                        <ActionButton
                                                            variant="primary-filled"
                                                            fullWidth
                                                            sx={{ mt: 1.5, width: '100%' }}
                                                            onClick={async () => {
                                                                if (applicationStatus) {
                                                                    try {
                                                                        dispatch(setLoading({ loading: true }));
                                                                        const response = await updateStudent(studentId, { application_status: applicationStatus });
                                                                        if (response?.data?.application_status) {
                                                                            showNotification('Application status updated', 'success');
                                                                        }
                                                                    } catch (err) {
                                                                        showNotification(errMsg(err), 'error');
                                                                    } finally {
                                                                        dispatch(setLoading({ loading: false }));
                                                                    }
                                                                } else {
                                                                    showNotification('Please select status', 'error');
                                                                }
                                                            }}
                                                        >
                                                            Update Status
                                                        </ActionButton>
                                                    </Box>
                                                </Grid>

                                                {/* Tasks Completed */}
                                                <Grid item xs={6} sm={3} md={4}>
                                                    <StatCard>
                                                        <Box>
                                                            <Typography fontWeight={700} fontSize={{ xs: 22, sm: 26 }} lineHeight={1}>
                                                                {stat?.total_completed_task || 0}
                                                            </Typography>
                                                            <Typography color="text.secondary" fontSize={12} mt={0.5}>
                                                                Tasks Completed
                                                            </Typography>
                                                        </Box>
                                                        <IconCircle bg="#DCFCE7">
                                                            <Box sx={{
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                borderRadius: '50%', border: '2px solid #00A63E', width: 22, height: 22
                                                            }}>
                                                                <Check sx={{ color: '#00A63E', fontSize: 13 }} />
                                                            </Box>
                                                        </IconCircle>
                                                    </StatCard>
                                                </Grid>

                                                {/* Tasks Pending */}
                                                <Grid item xs={6} sm={3} md={4}>
                                                    <StatCard>
                                                        <Box>
                                                            <Typography fontWeight={700} fontSize={{ xs: 22, sm: 26 }} lineHeight={1}>
                                                                {stat?.total_pending_task || 0}
                                                            </Typography>
                                                            <Typography color="text.secondary" fontSize={12} mt={0.5}>
                                                                Tasks Pending
                                                            </Typography>
                                                        </Box>
                                                        <IconCircle bg="#FFEDD4">
                                                            <WatchLaterOutlined sx={{ color: '#F54900', fontSize: 22 }} />
                                                        </IconCircle>
                                                    </StatCard>
                                                </Grid>

                                                <CollegeList colleges={colleges} />
                                            </Grid>
                                        </Fragment>
                                    )}
                                </Fragment>
                            }
                        />
                    )}
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default StudentDetail;